import React, { useEffect, useState } from "react";
import { getCreatorBanking, saveCreatorBanking } from "../../../services/creatorApi";

const EMPTY_FORM = {
  account_holder: "",
  bank_name: "",
  account_number: "",
  branch_code: "",
  account_type: "",
};

const ACCOUNT_TYPE_LABELS = {
  cheque: "Cheque / current",
  savings: "Savings",
  transmission: "Transmission",
};

export default function CreatorBankingCard() {
  const [banking, setBanking] = useState(null);
  const [banks, setBanks] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [notice, setNotice] = useState("");

  useEffect(() => {
    getCreatorBanking()
      .then((data) => {
        setBanking(data.banking);
        setBanks(data.banks || []);
        setAccountTypes(data.account_types || []);
        setEditing(!data.banking);
      })
      .catch((e) => setError(e.message || "Could not load banking details"))
      .finally(() => setLoading(false));
  }, []);

  const startEdit = () => {
    setForm({ ...EMPTY_FORM, ...(banking || {}) });
    setFieldErrors({});
    setNotice("");
    setEditing(true);
  };

  const update = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "bank_name") {
        const bank = banks.find((b) => b.name === value);
        if (bank?.branch_code) next.branch_code = bank.branch_code;
      }
      return next;
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});
    try {
      const data = await saveCreatorBanking(form);
      setBanking(data.banking);
      setEditing(false);
      setNotice(data.unchanged ? "No changes to save." : "Banking details saved.");
    } catch (e) {
      setError(e.message || "Could not save banking details");
      setFieldErrors(e.field_errors || {});
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const fieldError = (name) =>
    fieldErrors[name] ? <span className="creator-error">{fieldErrors[name]}</span> : null;

  return (
    <div className="training-card creator-banking-card">
      <h3 className="creator-section-title">Banking details</h3>

      {!editing && banking ? (
        <>
          <p className="creator-muted">We pay approved work by EFT into this account.</p>
          <dl className="creator-banking-summary">
            <dt>Account holder</dt>
            <dd>{banking.account_holder}</dd>
            <dt>Bank</dt>
            <dd>{banking.bank_name}</dd>
            <dt>Account number</dt>
            <dd>{banking.account_number_masked}</dd>
            <dt>Branch code</dt>
            <dd>{banking.branch_code}</dd>
            <dt>Account type</dt>
            <dd>{ACCOUNT_TYPE_LABELS[banking.account_type] || banking.account_type}</dd>
          </dl>
          {notice ? <p className="creator-muted">{notice}</p> : null}
          <button type="button" className="training-btn-secondary" onClick={startEdit}>
            Update banking details
          </button>
        </>
      ) : (
        <form className="profile-cv-form" onSubmit={onSubmit}>
          <p className="creator-muted">
            {banking
              ? "Changes are recorded and checked before your next payout."
              : "Add the account we should pay. Use an account in your own name."}
          </p>
          {error ? <p className="creator-error">{error}</p> : null}
          <div className="profile-form-grid">
            <label className="profile-field">
              <span className="profile-field__label">Account holder name</span>
              <input
                className="profile-input"
                value={form.account_holder}
                onChange={update("account_holder")}
                autoComplete="name"
                required
              />
              {fieldError("account_holder")}
            </label>
            <label className="profile-field">
              <span className="profile-field__label">Bank</span>
              <select
                className="profile-input"
                value={form.bank_name}
                onChange={update("bank_name")}
                required
              >
                <option value="">Choose your bank</option>
                {banks.map((bank) => (
                  <option key={bank.name} value={bank.name}>
                    {bank.name}
                  </option>
                ))}
              </select>
              {fieldError("bank_name")}
            </label>
            <label className="profile-field">
              <span className="profile-field__label">Account number</span>
              <input
                className="profile-input"
                value={form.account_number}
                onChange={update("account_number")}
                inputMode="numeric"
                autoComplete="off"
                required
              />
              {fieldError("account_number")}
            </label>
            <label className="profile-field">
              <span className="profile-field__label">Branch code</span>
              <input
                className="profile-input"
                value={form.branch_code}
                onChange={update("branch_code")}
                inputMode="numeric"
                autoComplete="off"
                required
              />
              <span className="profile-field__hint">
                Filled in for most banks. Check it if you chose Other.
              </span>
              {fieldError("branch_code")}
            </label>
            <label className="profile-field">
              <span className="profile-field__label">Account type</span>
              <select
                className="profile-input"
                value={form.account_type}
                onChange={update("account_type")}
                required
              >
                <option value="">Choose account type</option>
                {accountTypes.map((type) => (
                  <option key={type} value={type}>
                    {ACCOUNT_TYPE_LABELS[type] || type}
                  </option>
                ))}
              </select>
              {fieldError("account_type")}
            </label>
          </div>
          <div className="creator-banking-actions">
            <button type="submit" className="training-btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save banking details"}
            </button>
            {banking ? (
              <button
                type="button"
                className="training-btn-secondary"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      )}
    </div>
  );
}
